import {Box} from '@mui/material';
import {MutableRefObject, useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AffiliationType} from '../../backend/enums/affiliation-type';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {AuthContext} from '../../contexts/auth/context';
import {AuthContextType} from '../../contexts/auth/types';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {ScreenPaths} from '../../enums/screen-path';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableColumns} from '../../interfaces/paginated-table-columns';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {EntitiesRepository} from '../../repositories/entities';
import {UsersRepository} from '../../repositories/users';
import {AlertModal} from '../alert-modal/alert-modal';
import {ContentPage} from '../content-page/content-page';
import {PaginatedTable} from '../paginated-table/paginated-table';

// Columns IDs
enum ColumnIdentifier {
  EntityName = 'entity-name',
  EntityType = 'entity-type',
  AffiliationType = 'affiliation-type',
  Remove = 'remove',
}

/**
 * Listing of the affiliations of a user.
 *
 * Permissions are given by props.
 *
 * This form was created taking into account that it will exist under the senior/professional caregiver tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 *
 * Only used by system administrators at the moment.
 */
export const UserAffiliations = (props: {
  userId: string;
  userName: string;
  updateAffiliationType: boolean;
  removeAffiliation: boolean;
}) => {
  // Hold state
  const [removeDialogOpened, setRemoveDialogOpened] = useState<boolean>(false);
  const [updateDialogOpened, setUpdateDialogOpened] = useState<boolean>(false);

  // Hold ref
  const affiliationToRemoveRef: MutableRefObject<
    {entityId: string; entityName: string} | undefined
  > = useRef();
  const affiliationToUpdateRef: MutableRefObject<
    | {
        entityId: string;
        entityName: string;
        affiliationId: string;
        affiliationType: AffiliationType;
      }
    | undefined
  > = useRef();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const authContext: AuthContextType = useContext(AuthContext);

  // Translations
  const {t} = useTranslation();

  // Fetch affiliations
  const handleGetAffiliations = async (limit: number, skip: number) => {
    // Load affiliations
    const result: {totalCount: number; data: Array<AffiliationInfo>} =
      await UsersRepository.instance.getUserAffiliations(props.userId, skip, limit);

    // Process data
    const processedData: PaginatedTableData<AffiliationInfo> = result.data.map((obj) => {
      // Columns
      let columns: Array<{
        columnId: string;
        value?: string;
      }> = [
        {
          columnId: ColumnIdentifier.EntityName,
          value: obj.entity.name,
        },
        {
          columnId: ColumnIdentifier.EntityType,
          value: t(translations.entity_types[obj.entity.type]) || '',
        },
      ];
      if (props.updateAffiliationType) {
        columns.push({
          columnId: ColumnIdentifier.AffiliationType,
          value: obj.type,
        });
      }
      if (props.removeAffiliation) {
        columns.push({
          columnId: ColumnIdentifier.Remove,
        });
      }

      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        avatarUrl: obj.entity.picture,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Entities}/${obj.entity.id}`,
        columns: columns,
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    handlePageChange,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<AffiliationInfo>({
    fetchData: handleGetAffiliations,
  });

  const handleAskRemoveAffiliation = (affiliationId: string) => {
    // Get the row with the affiliation
    const affiliationRow = data.find((obj) => obj.id === affiliationId);

    // Set affiliation for later
    affiliationToRemoveRef.current = {
      entityId: affiliationRow?.item.entity.id || '',
      entityName: affiliationRow?.item.entity.name || '',
    };

    // Open modal
    setRemoveDialogOpened(true);
  };

  const handleDeclineRemoveAffiliation = () => {
    // Close modal
    setRemoveDialogOpened(false);

    // Clear
    affiliationToRemoveRef.current = undefined;
  };

  const handleConfirmRemoveAffiliation = () => {
    // Close modal
    setRemoveDialogOpened(false);

    // Remove affiliation
    handleRemoveAffiliation();
  };

  const handleRemoveAffiliation = async () => {
    try {
      // Make the request to the backend
      await UsersRepository.instance.deleteUserInEntity(
        affiliationToRemoveRef.current?.entityId || '',
        props.userId
      );

      // Refresh page
      if (data.length - 1 === 0 && currentPage > 1) {
        // Refresh to the previous page because the current page will be empty
        handleApplyFilters(currentPage - 1);
      } else {
        // Refresh the same page because we still have data on it
        handleApplyFilters();
      }

      // What if the current user just removed their affiliation with an entity?
      // We need to update the current user auth session if it's the case
      if (
        authContext.state.session?.id &&
        props.userId === authContext.state.session.id &&
        affiliationToRemoveRef.current &&
        affiliationToRemoveRef.current.entityId
      ) {
        // Remove affiliation in context
        authContext.removeAffiliation(affiliationToRemoveRef.current.entityId);
      }

      // Log
      console.debug(`🟢 Affiliation removed.`, {
        userId: props.userId,
        userName: props.userName,
        entityId: affiliationToRemoveRef.current?.entityId || '',
        entityName: affiliationToRemoveRef.current?.entityName || '',
      });

      // Clear
      affiliationToRemoveRef.current = undefined;
    } catch (error) {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_affiliations.errors.remove_affiliation_error_message),
      });

      // Log
      console.debug(`🔴 Could not remove the affiliation.`, {
        userId: props.userId,
        userName: props.userName,
        entityId: affiliationToRemoveRef.current?.entityId || '',
        entityName: affiliationToRemoveRef.current?.entityName || '',
        error: error,
      });

      // Clear
      affiliationToRemoveRef.current = undefined;
    }
  };

  const handleAskUpdateAffiliation = (affiliationId: string, affiliationType: string) => {
    // Get the row with the affiliation
    const affiliationRow = data.find((obj) => obj.id === affiliationId);

    // Set affiliation for later
    affiliationToUpdateRef.current = {
      entityId: affiliationRow?.item.entity.id || '',
      entityName: affiliationRow?.item.entity.name || '',
      affiliationId: affiliationId,
      affiliationType: affiliationType as AffiliationType,
    };

    // Open modal
    setUpdateDialogOpened(true);
  };

  const handleDeclineUpdateAffiliation = () => {
    // Close modal
    setUpdateDialogOpened(false);

    // Clear
    affiliationToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateAffiliation = () => {
    // Close modal
    setUpdateDialogOpened(false);

    // Update affiliation
    handleUpdateAffiliation();
  };

  const handleUpdateAffiliation = async () => {
    try {
      // Check affiliationToUpdateRef
      if (!affiliationToUpdateRef.current) {
        // Error
        throw new Error('Information missing.');
      }

      // Make the request to the backend
      await EntitiesRepository.instance.updateAffiliation(
        affiliationToUpdateRef.current.entityId,
        props.userId,
        {
          type: affiliationToUpdateRef.current.affiliationType,
        }
      );

      // Updated item
      let updatedItem = data.find(
        (obj) => obj.id === (affiliationToUpdateRef.current as any).affiliationId
      )?.item;
      if (updatedItem) {
        updatedItem.type = affiliationToUpdateRef.current.affiliationType;
      }

      // Update item in UI
      handleUpdateItem(
        affiliationToUpdateRef.current.affiliationId,
        [
          {
            updatedColumnId: ColumnIdentifier.AffiliationType,
            updatedColumnValue: affiliationToUpdateRef.current.affiliationType,
          },
        ],
        updatedItem
      );

      // If the user updated their own affiliation, we need to update
      // the session because some data may have changed.
      // If an error occurs, it's not a problem.
      if (authContext.state.session?.id && props.userId === authContext.state.session.id) {
        try {
          await authContext.refreshSessionData(
            authContext.state.session?.selectedRelationship?.id,
            authContext.state.session?.selectedAffiliation?.id
          );
        } catch (error) {
          // Error
          console.debug('🔴 Could not refresh session.', error);
        }
      }

      // Log
      console.debug(`🟢 Affiliation updated.`, {
        userId: props.userId,
        userName: props.userName,
        entityId: affiliationToUpdateRef.current?.entityId || '',
        entityName: affiliationToUpdateRef.current?.entityName || '',
        affiliationType: affiliationToUpdateRef.current?.affiliationType || '',
      });

      // Clear
      affiliationToUpdateRef.current = undefined;
    } catch (error) {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_affiliations.errors.update_affiliation_error_message),
      });

      // Log
      console.debug(`🔴 Could not update the affiliation.`, {
        userId: props.userId,
        userName: props.userName,
        entityId: affiliationToUpdateRef.current?.entityId || '',
        entityName: affiliationToUpdateRef.current?.entityName || '',
        affiliationType: affiliationToUpdateRef.current?.affiliationType || '',
        error: error,
      });

      // Clear
      affiliationToUpdateRef.current = undefined;
    }
  };

  // Columns
  let tableColumns: PaginatedTableColumns = [
    {
      columnId: ColumnIdentifier.EntityName,
      title: t(translations.user_affiliations.entity_name_header),
      flexGrow: 2,
    },
    {
      columnId: ColumnIdentifier.EntityType,
      title: t(translations.user_affiliations.entity_type_header),
      flexGrow: 1,
    },
  ];
  if (props.updateAffiliationType) {
    tableColumns.push({
      columnId: ColumnIdentifier.AffiliationType,
      title: t(translations.user_affiliations.affiliation_type_header),
      flexGrow: 1,
      select: {
        items: [
          {
            value: AffiliationType.Operator,
            label: t(translations.affiliation_types.operator),
          },
          {
            value: AffiliationType.Manager,
            label: t(translations.affiliation_types.manager),
          },
          {
            value: AffiliationType.Administrator,
            label: t(translations.affiliation_types.administrator),
          },
        ],
        handleChange: handleAskUpdateAffiliation,
      },
    });
  }
  if (props.removeAffiliation) {
    tableColumns.push({
      columnId: ColumnIdentifier.Remove,
      title: t(translations.user_affiliations.remove_affiliation_header),
      flexGrow: 1,
      button: {
        type: 'text-button',
        showColumnValue: false,
        textButton: {
          label: t(translations.user_affiliations.remove_affiliation_button),
          color: 'error',
          handleClick: handleAskRemoveAffiliation,
        },
      },
    });
  }

  return (
    <ContentPage>
      <Box sx={{marginTop: '16px'}}>
        <PaginatedTable
          avatar={{type: 'entity'}}
          emptyTableMessage={t(translations.user_affiliations.no_affiliations)}
          columns={tableColumns}
          rows={data}
          isLoading={isLoading}
          error={error}
          pagesCount={pagesCount}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          linkableRows={true}
        />
        <AlertModal
          id="confirm-remove-caregiver-affiliation"
          opened={removeDialogOpened}
          title={t(translations.user_affiliations.remove_affiliation_dialog_title, {
            entity: affiliationToRemoveRef.current?.entityName || '',
            user: props.userName || '',
          })}
          content={t(translations.user_affiliations.remove_affiliation_dialog_message, {
            entity: affiliationToRemoveRef.current?.entityName || '',
            user: props.userName || '',
          })}
          handleClose={handleDeclineRemoveAffiliation}
          actions={[
            {
              label: t(translations.user_affiliations.remove_affiliation_dialog_action_yes_button),
              type: 'destructive',
              handleClick: handleConfirmRemoveAffiliation,
            },
            {
              label: t(translations.user_affiliations.remove_affiliation_dialog_action_no_button),
              type: 'neutral',
              handleClick: handleDeclineRemoveAffiliation,
            },
          ]}
        />
        <AlertModal
          id="confirm-update-caregiver-affiliation"
          opened={updateDialogOpened}
          title={t(translations.user_affiliations.update_affiliation_dialog_title, {
            entity: affiliationToUpdateRef.current?.entityName || '',
            user: props.userName || '',
          })}
          content={t(translations.user_affiliations.update_affiliation_dialog_message, {
            entity: affiliationToUpdateRef.current?.entityName || '',
            type: affiliationToUpdateRef.current?.affiliationType
              ? t(translations.affiliation_types[affiliationToUpdateRef.current.affiliationType])
              : '',
          })}
          handleClose={handleDeclineUpdateAffiliation}
          actions={[
            {
              label: t(translations.user_affiliations.update_affiliation_dialog_action_no_button),
              type: 'neutral',
              handleClick: handleDeclineUpdateAffiliation,
            },
            {
              label: t(translations.user_affiliations.update_affiliation_dialog_action_yes_button),
              type: 'nondestructive',
              handleClick: handleConfirmUpdateAffiliation,
            },
          ]}
        />
      </Box>
    </ContentPage>
  );
};
